import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { AlertTriangle, Check } from "lucide-react";

const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

// The 6 CRM values are retail BRANCHES, not SAP companies. Several branches can roll
// up to the same company, so duplicate schema selections are legitimate here.
export default function StoreMapping() {
    const [stores, setStores] = useState([]);
    const [schemas, setSchemas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = async () => {
        setLoading(true);
        setErrored(false);
        try {
            const { data } = await api.get("/crm/store-map");
            setStores(Array.isArray(data?.stores) ? data.stores : []);
            setSchemas(Array.isArray(data?.available_schemas) ? data.available_schemas : []);
            setDirty(false);
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const setSchema = (store, schema_name) => {
        setStores((prev) => prev.map((r) => (r.store === store ? { ...r, schema_name } : r)));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/crm/store-map", {
                stores: stores.map((r) => ({
                    store: r.store,
                    schema_name: r.schema_name || "",
                    display_name: (schemas.find((x) => x.schema_name === r.schema_name) || {}).display_name || "",
                })),
            });
            toast.success("Branch mapping saved");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const unmapped = stores.filter((r) => !r.schema_name);

    // How many branches point at each company — duplicates are fine, but the operator
    // should see them, because SAP reports revenue per company, not per branch.
    const perSchema = stores.reduce((acc, r) => {
        if (r.schema_name) acc[r.schema_name] = (acc[r.schema_name] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-store-map-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM · Administration</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Branch → SAP Company</h1>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Manual sales are recorded against a retail branch. SAP reports revenue per company.
                        This mapping is the join between the two — getting a pair wrong attributes one
                        company's revenue to another, so nothing is guessed for you.
                    </p>
                </div>
                <button
                    onClick={save}
                    disabled={saving || !dirty}
                    className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50"
                    data-testid="store-map-save"
                >
                    {saving ? "Saving…" : dirty ? "Save Mapping" : "Saved"}
                </button>
            </div>

            {!loading && unmapped.length > 0 && (
                <div className="sc-card p-4 flex items-start gap-3 border-amber-500/40">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm">
                        <div className="font-medium text-amber-600">
                            {unmapped.length} branch{unmapped.length === 1 ? "" : "es"} not mapped yet
                        </div>
                        <div className="text-muted-foreground text-xs mt-0.5">
                            Sales recorded against {unmapped.map((r) => r.store).join(", ")} will show as
                            “no SAP mapping” in the comparison rather than being reported as zero revenue.
                        </div>
                    </div>
                </div>
            )}

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[700px]">
                        <thead>
                            <tr>
                                <th>CRM Branch</th>
                                <th>SAP Company</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={3} className="text-center py-8 text-muted-foreground">Loading…</td></tr>
                            )}
                            {!loading && errored && (
                                <tr><td colSpan={3} className="text-center py-8 text-muted-foreground">Couldn't load the mapping.</td></tr>
                            )}
                            {!loading && !errored && stores.map((r) => (
                                <tr key={r.store} data-testid={`store-map-row-${r.store}`}>
                                    <td className="font-medium">{s(r.store)}</td>
                                    <td>
                                        <select
                                            className="sc-input md:w-72"
                                            value={r.schema_name || ""}
                                            onChange={(e) => setSchema(r.store, e.target.value)}
                                            data-testid={`store-map-select-${r.store}`}
                                        >
                                            <option value="">— not mapped —</option>
                                            {schemas.map((sc) => (
                                                <option key={sc.schema_name} value={sc.schema_name}>
                                                    {sc.display_name} ({sc.schema_name})
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="text-xs">
                                        {r.schema_name ? (
                                            <span className="inline-flex items-center gap-1 text-emerald-600">
                                                <Check className="w-3.5 h-3.5" />
                                                {perSchema[r.schema_name] > 1
                                                    ? `shares this company with ${perSchema[r.schema_name] - 1} other branch${perSchema[r.schema_name] - 1 === 1 ? "" : "es"}`
                                                    : "mapped"}
                                            </span>
                                        ) : (
                                            <span className="text-amber-600">not mapped</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-xs text-muted-foreground">
                Several branches may point at the same SAP company — that is expected. Where they do, the
                comparison reports SAP revenue at company level and breaks the manual figures down by branch
                underneath it, because SAP cannot split a company's revenue across branches.
            </p>
        </div>
    );
}
